"use client"

import { useState, useEffect, useRef, useCallback, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { OverseedingForm, fmtLbs, rangeLabel, type OverseedingInitialValues } from "@/components/tools/overseeding-form"
import {
  HeadlineHero,
  RelatedTools,
  ResultEmailCaptureCard,
  ResultSuccessCard,
  ToolPageLoadingFallback,
  ToolPageShell,
  ToolWideRegion,
} from "@/components/tools/shared"
import type { ResultSuccessCardStat } from "@/components/tools/shared"
import { OVERSEED_GRASSES, type OverseedGrassId, type OverseedingResult } from "@/lib/overseeding-calculator"
import { analytics, stashEmailCaptureAttribution, toolNavigationAnalytics } from "@/lib/analytics"
import { buildRelatedToolUrl, buildToolUrl } from "@/lib/result-tool-urls"
import { CalendarClock, Package, Scale } from "lucide-react"
import { useCalculatorStarted } from "@/hooks/useCalculatorStarted"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"

const GRASS_PARAM: Record<string, OverseedGrassId> = {
  fescue: "fescue",
  tallfescue: "fescue",
  kbg: "kbg",
  kentuckybluegrass: "kbg",
  bluegrass: "kbg",
  rye: "rye",
  ryegrass: "rye",
  perennialryegrass: "rye",
  fine: "fine",
  finefescue: "fine",
  bermuda: "bermuda",
  bermudagrass: "bermuda",
  zoysia: "zoysia",
  zoysiagrass: "zoysia",
  centipede: "centipede",
  centipedegrass: "centipede",
  staug: "staug",
  staugustine: "staug",
  staugustinegrass: "staug",
  annualrye: "annualrye",
  annualryegrass: "annualrye",
  winterrye: "annualrye",
  italianryegrass: "annualrye",
  coolmix: "coolmix",
  coolseasonmix: "coolmix",
  coolseasonblend: "coolmix",
  mixedcoolseasonblend: "coolmix",
  carpetgrass: "carpetgrass",
  commoncarpetgrass: "carpetgrass",
  axonopusfissifolius: "carpetgrass",
  axonopusaffinis: "carpetgrass",
}

function normalizeGrass(raw: string): OverseedGrassId | undefined {
  const key = raw.toLowerCase().replace(/[-_.\s]+/g, "")
  return GRASS_PARAM[key]
}

function OverseedingContent({ picksSlot }: { picksSlot?: React.ReactNode }) {
  const searchParams = usePrerenderSafeSearchParams()
  const [initialValues, setInitialValues] = useState<OverseedingInitialValues | null>(null)
  const [liveResult, setLiveResult] = useState<OverseedingResult | null>(null)
  const [lawnSize, setLawnSize] = useState(5000)
  const trackedRef = useRef(false)
  // calculator_started on first input — result is gated on a known region (ZIP).
  const startedRef = useCalculatorStarted('overseeding')

  const handleResultChange = useCallback((result: OverseedingResult, size: number) => {
    setLiveResult(result)
    setLawnSize(size)
  }, [])

  // URL prefill: ?zip & size & grass
  useEffect(() => {
    const zipParam = searchParams.get("zip")
    const sizeParam = searchParams.get("size")
    const grassParam = searchParams.get("grass")

    if (!zipParam && !sizeParam && !grassParam) return

    const size = sizeParam ? parseInt(sizeParam, 10) : undefined
    const next: OverseedingInitialValues = {
      zipCode: zipParam && /^\d{5}$/.test(zipParam) ? zipParam : undefined,
      lawnSize: size && !isNaN(size) ? size : undefined,
      grassType: grassParam ? normalizeGrass(grassParam) : undefined,
    }
    setInitialValues(next)
  }, [searchParams])

  // Fire calculator_used once, when a usable result (known region) first appears.
  useEffect(() => {
    if (!liveResult || trackedRef.current || !liveResult.regionKnown) return
    trackedRef.current = true
    analytics.calculatorUsed("overseeding", {
      inputs: {
        grass_type: liveResult.grassId,
        region: liveResult.region,
      },
      result: {
        mode: liveResult.mode,
        verdict: liveResult.verdict ?? undefined,
        lbs: liveResult.seed?.lbsRecommended ?? undefined,
      },
      source: "live_update",
    })
  }, [liveResult])

  return (
    <ToolPageShell breadcrumbLabel="Overseeding Calculator" rootRef={startedRef}>
      <HeadlineHero
        eyebrow="Plan + quantity"
        eyebrowIcon={<Scale className="h-3.5 w-3.5" aria-hidden="true" />}
        title="How much seed to overseed, and"
        accent="exactly when"
        subtitle="Enter your ZIP, grass type, and lawn size. You'll get the pounds of seed to buy, the bags that add up to it, and your best overseeding window with a countdown."
      />

      <ToolWideRegion>
        <OverseedingForm
          initialValues={initialValues}
          onResultChange={handleResultChange}
          resultTargetId="overseeding-result"
        />
      </ToolWideRegion>

      {liveResult && liveResult.regionKnown && (
        <div id="overseeding-result" className="mt-8 scroll-mt-20 space-y-6">
          <OverseedingSuccessCard result={liveResult} lawnSize={lawnSize} zip={initialValues?.zipCode} />
          <ResultEmailCaptureCard
            source="tool_result_overseeding"
            toolDisplayName="overseeding plan"
            subcopy="A reminder when your overseeding window opens, sent to your inbox."
            zipCode={initialValues?.zipCode}
            accountLead={{
              redirectTo: buildToolUrl("overseeding-calculator", {
                zip: initialValues?.zipCode,
                lawnSize: initialValues?.lawnSize,
              }),
              headline: "Save my overseeding plan",
              subcopy: "Keep this overseeding plan in a free account and track your lawn over time.",
              onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_overseeding"),
            }}
            successSubcopy="Your lawn tips are on the way. Your overseeding plan stays right here to revisit anytime."
            onCaptured={() => {}}
          />
        </div>
      )}

      {/* Products never sit above a tool. Seed picks render below the form
          and its result, from the server shell so the anchors reach the
          served HTML. */}
      {picksSlot && (liveResult?.grassId ?? initialValues?.grassType) !== 'carpetgrass' && (
        <div className="mt-8">{picksSlot}</div>
      )}

      <RelatedTools
        className="mt-10"
        currentToolId="overseeding-calculator"
        prefillValues={{
          zip: initialValues?.zipCode,
          lawnSize: initialValues?.lawnSize,
        }}
      />
    </ToolPageShell>
  )
}

/**
 * Peak frame for the overseeding result: pounds of seed (or plug trays for
 * St. Augustine) with the grass, lawn size, and window named, plus one next
 * step. Carpetgrass has no verified seed rate, so it gets the soil-temperature
 * guidance and no number.
 */
function OverseedingSuccessCard({
  result,
  lawnSize,
  zip,
}: {
  result: OverseedingResult
  lawnSize: number
  zip?: string
}) {
  const sizeLabel = lawnSize.toLocaleString()
  const focus = result.focusWindow
  const windowText = focus ? rangeLabel(focus) : null
  const grassName = OVERSEED_GRASSES[result.grassId].display
  const impressionRef = useImpressionEvent<HTMLDivElement>("overseeding-calculator", undefined, {
    emit: () =>
      toolNavigationAnalytics.relatedToolsViewed({
        sourceTool: "overseeding-calculator",
        placement: "success_card",
        toolCount: 1,
      }),
  })
  const share = {
    url: buildToolUrl("overseeding-calculator", { zip, grassType: grassName, lawnSize }),
    title: "Overseeding Plan",
    text: `Overseeding plan for my ${result.grassLabel} lawn`,
    trackingId: "overseeding-calculator",
  }

  const ctaFor = (targetTool: string, label: string, reason: string) => ({
    href: buildRelatedToolUrl(targetTool, "overseeding-calculator", {
      zip,
      grassType: grassName,
      lawnSize,
    }),
    label,
    onClick: () =>
      toolNavigationAnalytics.relatedToolClicked({
        sourceTool: "overseeding-calculator",
        targetTool,
        rank: 1,
        placement: "success_card",
        recommendationReason: reason,
        prefillFields: zip ? "zip,grass,size" : "grass,size",
      }),
  })

  if (result.seedGuidance && !result.seed) {
    return (
      <div ref={impressionRef}>
        <ResultSuccessCard
          eyebrow="Check your soil first"
          interpretation={result.seedGuidance}
          cta={ctaFor("soil-temperature", "Check my soil temperature", "Seed once soil reaches the label temperature")}
          share={share}
        />
      </div>
    )
  }

  if (result.mode === "stolons-only" && result.plugTrays != null) {
    return (
      <div ref={impressionRef}>
        <ResultSuccessCard
          heroValue={result.plugTrays.toString()}
          heroUnit={result.plugTrays === 1 ? "tray" : "trays"}
          heroLabel="of St. Augustine plugs for thin areas"
          interpretation={
            <>
              Plug the thin spots in your {sizeLabel} sq ft {result.grassLabel} lawn 12 inches apart, from late spring
              through mid-summer. Seed from a bag won&apos;t take on this grass.
            </>
          }
          cta={ctaFor("sod-calculator", "Price sod for the bare areas", "Sod larger bare areas instead of plugging")}
          share={share}
        />
      </div>
    )
  }

  if (!result.seed) return null

  const lbs = fmtLbs(result.seed.lbsRecommended)
  const stats: ResultSuccessCardStat[] = [
    { icon: Scale, label: "Per 1,000 sq ft", value: `${result.seed.ratePer1000} lb` },
    { icon: Package, label: "Bags to buy", value: result.seed.bagCombo },
  ]
  if (windowText) stats.push({ icon: CalendarClock, label: "Best window", value: windowText })

  return (
    <div ref={impressionRef}>
      <ResultSuccessCard
        heroValue={lbs}
        heroUnit="lbs"
        heroLabel={`of ${result.seed.coated ? "coated " : ""}${result.grassLabel.toLowerCase()} seed`}
        stats={stats}
        interpretation={
          <>
            Spread {lbs} lbs of {result.grassLabel} seed across your {sizeLabel} sq ft lawn
            {windowText ? <>. Your best {result.region} window is {windowText}.</> : "."}
          </>
        }
        cta={ctaFor("aeration-timing-tool", "Time my core aeration", "Aerate first so overseed makes solid soil contact")}
        share={share}
      />
    </div>
  )
}

export function OverseedingCalculatorPageContent({ picksSlot }: { picksSlot?: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <>
          <ToolPageLoadingFallback toolId="overseeding-calculator" />
          {/* Keeps the picks on screen if the island ever suspends. */}
          {picksSlot && (
            <div className="mx-auto max-w-5xl px-4 pb-8 md:px-6 lg:px-8">{picksSlot}</div>
          )}
        </>
      }
    >
      <SearchParamsHydrationGate dateSensitive>
        <OverseedingContent picksSlot={picksSlot} />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
